<template>
  <b-card
    no-body
    id="v-step-4"
    class="h-100 w-100"
    border-variant="primary"
    text-variant="white"
  >
    <b-card-header header-bg-variant="primary">
      List of Instruments
      <b-badge variant="light">{{rows}} found at {{areaName}}</b-badge>
    </b-card-header>
    <!--The card deck for the list of instruments-->
    <b-container fluid class="h-100" style="color: black !important;">
      <b-row v-if="rows === 0" class="h-100 flex-column justify-content-center">
        <b-col cols="12" class="h-100 text-center p-4">
          <h4>
            No instruments found
          </h4>
          <p>
            Please try different filters
          </p>
        </b-col>
      </b-row>
      <b-row v-if="!isHidden && rows > 0" style="height: 10%;">
        <b-col class="pt-3">
          <b-pagination
            class="card-listing-pagination"
            v-model="currentPage"
            :total-rows="rows"
            :per-page="perPage"
            aria-controls="card-listing"
            size="sm"
            align="fill"
            :first-number="true"
            :last-number="true"
            first-text="First"
            prev-text="Prev"
            next-text="Next"
            last-text="Last"
          ></b-pagination>
        </b-col>
      </b-row>
      <b-row v-if="rows > 0" :style="listRowStyle" class="flex-column justify-content-center">
        <b-col class="h-100" style="overflow-y: auto;">
          <b-list-group class="h-100" id="card-listing" v-if="!isHidden" flush>
            <b-list-group-item
              id="v-step-5"
              v-for="inst in filtItems"
              :key="inst.reference_designator"
              flush
            >
              <b-row class="pb-1">
                <h6>
                  <!-- Inst icons in separate component -->
                  <inst-icons :instAT="inst.asset_type"></inst-icons>
                  <strong style="font-size: 0.9375em;">{{inst.formal_name}}</strong>
                  <!--Status indicator icon using nereus information-->
                  <status-indicator
                    :instRD="inst.reference_designator"
                  ></status-indicator>
                </h6>
              </b-row>
              <b-row class="pb-2" v-if="inst.discontinued">
                <b-badge
                  id="discontinued-badge"
                  pill
                  variant="secondary"
                >Discontinued</b-badge>
              </b-row>
              <b-row>
                <h6>
                  <small style="font-size: 0.75em;">
                    {{siteName(inst.site_rd)}} | {{infraName(inst.infra_rd)}}
                    <br>
                    {{inst.reference_designator}}
                  </small>
                </h6>
              </b-row>
              <b-row v-if="inst.nameset && inst.nameset.length > 0">
                <!--Button to show listing of parameters if data has been harvested-->
                <b-button
                  variant="link"
                  size="sm"
                  v-b-toggle.collapse="collapseID(inst.reference_designator)"
                >Click to view parameters
                </b-button>
              </b-row>
              <b-row
                v-if="inst.nameset && inst.nameset.length > 0"
                style="color: #383838; font-size: 0.75em; max-height: 150px; overflow-y: auto;"
              >
                <!-- 12px/16px = 0.875em, (1em = 16px) -->
                <!--The parameter listing through the nameset on the metadata
                endpoint. Fixes the spacing between parameters-->
                <!-- Using value -->
                <b-collapse :id="`${inst.reference_designator}-collapse`" class="mt-2">
                  <span v-for="param in inst.nameset"
                      :key="`${param}--${inst.reference_designator}`">
                    &nbsp; {{param}} <br>
                  </span>
                </b-collapse>
              </b-row>
              <b-row>
                <div id="v-step-7" style="display: inline;" class="p-1">
                  <b-button
                    @click.prevent="openInstCard(inst.reference_designator, inst.wp_mapping)"
                    size="sm"
                    variant="primary"
                  >Details</b-button>
                </div>
                <div id="v-step-6" style="display: inline;" class="p-1">
                  <b-btn
                    :id="`${inst.reference_designator}--dataset`"
                    @click.prevent="addToTrace"
                    size="sm"
                    :variant="btnVariant(inst.harvest_flag, inst.discontinued)"
                    v-if="!isSelected(inst)"
                    :disabled="!inst.harvest_flag || inst.discontinued"
                    :title="btnPopoverText(inst.harvest_flag, inst.discontinued)"
                  >
                    Add to dataset
                  </b-btn>
                  <b-btn :id="`${inst.reference_designator}--dataset`"
                    @click.prevent="removeFromTrace"
                    size="sm"
                    variant="danger"
                    v-if="isSelected(inst)"
                  >Remove from dataset</b-btn>
                </div>
              </b-row>
            </b-list-group-item>
          </b-list-group>
          <!--Details of instruments will show when user clicks "Details" on instrument card-->
          <div v-if="isHidden">
            <b-overlay
              :show="loadPage"
              rounded="sm"
              spinner-small
              spinner-variant="primary"
            >
              <b-card
                no-body
                class="overflow-hidden"
                style="border: none"
                v-for="inst in instrumentCard"
                :key="inst.reference_designator"
              >
                <b-row>
                  <b-col cols="12">
                    <b-button
                      size="sm"
                      variant="link"
                      v-on:click="isHidden = !isHidden"
                      class="mr-2 pt-2"
                    >
                      <i class="far fa-arrow-alt-circle-left"></i>
                      Back
                    </b-button>
                    <!--Instrument image from static page-->
                    <b-card-img-lazy
                      :id="`${inst.reference_designator}-traceid`"
                      v-bind="mainProps"
                      :show="!loadingImg"
                      :src="instImg"
                      alt="Image"
                    ></b-card-img-lazy>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="12">
                    <b-card-body>
                      <b-card-text>
                        <h6>
                          <!--Inst icons in separate component -->
                          <inst-icons :instAT="inst.asset_type"></inst-icons>
                          <strong>{{inst.formal_name}}</strong>
                          <p
                            style="font-size: 0.875em;"
                          >
                            <!-- 14px/16px = 0.875em, (1em = 16px) -->
                            Instrument Code: {{inst.group_code}}
                            <br>
                            {{siteName(inst.site_rd)}} | {{infraName(inst.infra_rd)}}
                          </p>
                        </h6>
                        <div class="instrument-attribute">
                          <strong>Instrument Name: </strong>
                          {{inst.instrument_name}}
                        </div>
                        <!--Status indicator text using nereus information-->
                        <div class="instrument-attribute">
                          <strong>Instrument Status: </strong>
                          <status-text
                            :instRD="inst.reference_designator"
                          ></status-text>
                        </div>
                        <div class="instrument-attribute">
                          <strong>Reference Designator: </strong>
                          {{inst.reference_designator}}
                        </div>
                        <div class="instrument-attribute">
                          <strong>Description: </strong>
                          {{inst.description}}
                        </div>
                        <div class="instrument-attribute">
                          <strong>Model: </strong>
                          {{inst.model ? inst.model : "N/A"}}
                        </div>
                        <div class="instrument-attribute">
                          <strong>Manufacturer: </strong>
                          {{inst.manufacturer ? inst.manufacturer : "N/A"}}
                        </div>
                        <div class="instrument-attribute">
                          <strong>Owner: </strong>
                          {{inst.owner ? inst.owner : "N/A"}}
                        </div>
                        <br>
                        <b-button
                          variant="primary"
                          size="sm"
                          :href="`${ooiURL}${inst.instrument_url}`"
                          target="_blank"
                        >
                          Learn More
                          <i class="fas fa-external-link-alt fa-sm"></i>
                        </b-button>
                        <b-button
                          class="availability-btn"
                          variant="primary"
                          size="sm"
                          v-b-tooltip.hover
                          :disabled="!inst.harvest_flag"
                          :title="inst.harvest_flag ?
                          'Cloud data available.' :
                          'No cloud data available.'"
                          @click="showDAModal(`${inst.reference_designator}`,
                      inst.instrument_name)"
                        >Data Availability
                          <!-- <b-badge class="availability-badge">Coming<br>soon!</b-badge> -->
                        </b-button>
                        <b-spinner
                          style="width: 1rem; height: 1rem;"
                          variant="primary"
                          label="Spinning"
                          v-if="daModalLoading"
                        ></b-spinner>
                      </b-card-text>
                    </b-card-body>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="12">
                    <b-tabs content-class="mt-3" fill>
                      <b-tab>
                        <template v-slot:title>
                          Deployments
                          <b-badge pill variant="info">{{deployments.length}}</b-badge>
                          <b-spinner v-if="loadingDeployments"
                          class="ml-1" type="border" small></b-spinner>
                        </template>
                        <p v-if="deployments.length === 0" class="text-center">
                          <strong>No deployments found.</strong>
                        </p>
                        <b-table
                          v-if="deployments.length > 0"
                          hover
                          :items="deployments"
                          small
                          stacked
                        >
                        </b-table>
                      </b-tab>
                      <b-tab>
                        <template v-slot:title>
                          Annotations
                          <b-badge pill variant="info">{{annotations.length}}</b-badge>
                          <b-spinner v-if="loadingAnnotations"
                          class="ml-1" type="border" small></b-spinner>
                        </template>
                        <p v-if="annotations.length === 0" class="text-center">
                          <strong>No annotations found.</strong>
                        </p>
                        <b-table
                          v-if="annotations.length > 0"
                          hover
                          :items="annotations"
                          small
                          stacked>
                        </b-table>
                      </b-tab>
                    </b-tabs>
                  </b-col>
                </b-row>
                <br>
              </b-card>
            </b-overlay>
          </div>
        </b-col>
      </b-row>
    </b-container>
    <da-modal ref="da-modal"></da-modal>
  </b-card>
</template>
<script>
import _ from 'lodash';
import axios from 'axios';
import { DateTime } from 'luxon';
import {
  mapMutations, mapState, mapActions, mapGetters,
} from 'vuex';
import {
  OOIURL, APIBASEURL, APIMETADATA, APIINSTRUMENTS,
} from '@/utils/constants';
import noImgPlaceholder from '../../../assets/img/no-img-available.png';
import DAModal from '../../DAModal';
import InstIconsVue from '../../maps/instIcons';
import StatusIndicatorVue from '../../statusIndicators/StatusIndicatorDisplay';
import StatusIndicatorTextVue from '../../statusIndicators/StatusIndicatorText';

export default {
  components: {
    'da-modal': DAModal,
    'status-indicator': StatusIndicatorVue,
    'status-text': StatusIndicatorTextVue,
    'inst-icons': InstIconsVue,
  },
  data() {
    return {
      error: null,
      noImgPlaceholder,
      ooiURL: OOIURL,
      perPage: 4,
      currentPage: 1,
      instrumentsParams: [], // all instruments
      instrumentCard: [],
      isHidden: false,
      listRowStyle: {
        height: '90%',
      },
      loadPage: false,
      timeout: null,
      deployments: [],
      annotations: [],
      loadingDeployments: false,
      loadingAnnotations: false,
      loadingImg: false,
      instImg: '',
      mainProps: { blank: true, blankColor: '#777', class: 'm1' },
      params: { include_params: true, area: '' },
      siteFilter: [],
      infraFilter: [],
      instFilter: [],
      selectedAreaDefault: 'axial-base', // default research site
    };
  },
  computed: {
    // Gets the posts that are in the store
    ...mapState({
      researchAreas: (state) => state.areas.features,
      instruments: (state) => state.instruments,
      infrastructures: (state) => state.infrastructures,
      sites: (state) => state.sites.features,
      daModal: (state) => state.daModal,
      daModalLoading: (state) => state.daModalLoading,
    }),
    ...mapState('visualization/traces', {
      selectedInstruments: (state) => state.selectedInstruments,
    }),
    ...mapGetters('visualization/charts', {
      getChart: 'find',
      charts: 'list',
      chartsLength: 'count',
    }),
    ...mapGetters('visualization/traces', {
      getTraces: 'findByUUID',
      getTrace: 'find',
      traceId: 'currentId',
      getInstruments: 'findSelectedInstruments',
    }),
    selectedRefs() {
      return this.selectedInstruments.map((o) => o.ref);
    },
    datasetId() {
      return this.$route.query.d;
    },
    filtItems() {
      const indexStart = (this.currentPage - 1) * this.perPage;
      const indexEnd = this.perPage * this.currentPage;
      return this.instrumentsParams.slice(indexStart, indexEnd);
    },
    rows() {
      return this.instrumentsParams.length;
    },
    // Gets the formal area name from the area endpoint
    areaName() {
      return _.find(this.researchAreas, [
        'properties.reference_designator', this.$route.query.area,
      ]).properties.area_name;
    },
    uuid() {
      return this.$route.params.id;
    },
    chart() {
      if (this.chartsLength === 0) {
        return null;
      }
      return this.getChart(this.uuid);
    },
    traces() {
      return this.getTraces(this.uuid);
    },
    chartObj() {
      return this.chart.chart;
    },
    currentTrace() {
      if (this.traces.length > 0) {
        return this.getTrace(this.$route.query.d);
      }
      return null;
    },
  },
  async created() {
    await this.queryInstruments(this.$route.query);
  },
  async mounted() {
    // Query from the route
    // await this.queryInstruments(this.$route.query)
    this.setdaModal(this.$refs['da-modal']);
  },
  watch: {
    async $route(to, from) {
      // As route switches get the necessary queries
      const filterQueries = ['area', 'infrastructure', 'instrument'];
      const toQuery = filterQueries.map((k) => ({ [k]: k in to.query ? to.query[k] : null }));
      const fromQuery = filterQueries.map((k) => ({ [k]: k in from.query ? from.query[k] : null }));
      if (!_.isEqual(toQuery, fromQuery)) {
        // If there's a change in the area, infra or inst query
        // then filter the instruments
        await this.queryInstruments(to.query);
      }
    },
    isHidden(newVal) {
      this.listRowStyle = {
        height: newVal ? '100%' : '90%',
      };
    },
    // async currentPage() {
    //   this.filterItems();
    // },
  },
  methods: {
    ...mapActions('visualization/traces', [
      'addInstrument',
      'removeInstrument',
      'resetTrace',
    ]),
    ...mapActions('visualization/charts', [
      'resetPlotLayout',
      'setHasDataFlag',
    ]),
    ...mapMutations([
      'setdaModalLoading',
      'setdaModal',
    ]),
    btnVariant(harvestFlag, discontinued) {
      if (!harvestFlag || discontinued) {
        return 'secondary';
      }
      return 'primary';
    },
    btnPopoverText(harvestFlag, discontinued) {
      if (discontinued) {
        return 'Instrument has been discontinued.';
      }
      if (!harvestFlag) {
        return 'Not available for plotting at this time.';
      }
      return null;
    },
    /**
     * Queries instruments from specific area
     * @param {Object} query - The query object from vue-router
     */
    async queryInstruments(query) {
      /** The request parameters for instruments API endpoint */
      const requestParams = {
        area: query.area,
        infrastructure: query.infrastructure,
        group: query.instrument,
        include_params: true,
      };

      /** Get instruments from specified area */
      const response = await this.getInstrumentListing(requestParams);
      const instruments = response.data;
      this.instrumentsParams = _.sortBy(instruments, 'formal_name');
      // this.filterInstList(instruments);
    },
    /**
     * Checks whether instrument is selected
     * @param {Object} inst - Instrument object
     */
    isSelected(inst) {
      const foundInst = _.find(this.selectedInstruments, {
        id: this.currentTrace.id,
        ref: inst.reference_designator,
      });
      if (foundInst) {
        return true;
      }
      return false;
    },
    // parseInstrumentsFromQ(items, query) {
    //   const traceId = JSON.parse(query.d);
    //   const parameters = [];
    //   ['x', 'y', 'color'].forEach((k) => {
    //     if (k in query) {
    //       if (query[k] !== 'time') {
    //         parameters.push(query[k]);
    //       }
    //     }
    //   });
    //   if (parameters.length > 0) {
    //     let instruments = parameters.map((p) => p.split('-').slice(0, 4).join('-'));
    //     instruments = _.uniq(instruments);

    //     return items.map((i) => {
    //       const ref = i.reference_designator;
    //       if (instruments.includes(i.reference_designator)) {
    //         if (!this.selectedRefs.includes(ref)) {
    //           this.addInstrument({
    //             id: traceId,
    //             ref,
    //           });
    //         }
    //         return { ...i, isSelected: true };
    //       }
    //       return i;
    //     });
    //   }
    //   return items;
    // },
    collapseID(ref) {
      return `${ref}-collapse`;
    },
    showDAModal(rd, instrumentTitle) {
      this.setdaModalLoading();
      // show instrument data avail. modal
      this.daModal.show(rd, instrumentTitle);
    },
    filterItems() {
      const indexStart = (this.currentPage - 1) * this.perPage;
      const indexEnd = this.perPage * this.currentPage;
      this.filtItems = this.instrumentsParams.slice(indexStart, indexEnd);
    },
    // Gets the formal site name from the sites endpoint
    siteName(siteRd) {
      const siteName = _.filter(this.sites, [
        'properties.reference_designator', siteRd,
      ]);
      return siteName[0].properties.site_name;
    },
    // Gets the formal infra name from the infrastructure endpoint
    infraName(infraRd) {
      const infraName = _.filter(this.infrastructures, [
        'reference_designator', infraRd,
      ]);
      return infraName[0].infraName;
    },
    /**
     * Get instruments from API
     * @param {Object} params - The parameters to be passed into the endpoint
     */
    async getInstrumentListing(params) {
      return axios.get(`${APIBASEURL}/${APIMETADATA}/${APIINSTRUMENTS}`, { params });
    },
    /**
     * Filters through listing of instruments.
     * Sets the instrument listing to filtered list
     * @param {Array} instruments - An array of instrument objects from API
     */
    // async filterInstList(instruments) {
    //   if (_.isArray(instruments)) {
    //     /**
    //      * Sometimes string?
    //      * Anyways, this only allow arrays.
    //      */
    //     let updateInstList = [];
    //     instruments.forEach((feature) => {
    //       // if (feature.harvest_flag) {
    //       //   updateInstList.push(feature);
    //       // }
    //       updateInstList.push(feature);
    //     });
    //     _.sortBy(updateInstList, ['reference_designator']);
    //     const { query } = this.$route;

    //     /**
    //      * Filter instruments by area
    //      */
    //     updateInstList = _.filter(updateInstList, (o) => {
    //       if (
    //         query.area.split(',').filter(Boolean).includes(o.area_rd)
    //       ) {
    //         return o;
    //       }
    //       return null;
    //     });

    //     /**
    //      * Filter instruments by infrastructure
    //      */
    //     if ('infrastructure' in query) {
    //       updateInstList = _.filter(updateInstList, (o) => {
    //         if (
    //           query.infrastructure.split(',').filter(Boolean).includes(o.infra_rd)
    //         ) {
    //           return o;
    //         }
    //         return null;
    //       });
    //     }

    //     /**
    //      * Filter instruments by instrument
    //      */
    //     if ('instrument' in query) {
    //       updateInstList = _.filter(updateInstList, (o) => {
    //         if (
    //           query.instrument.split(',').filter(Boolean).includes(o.group_code)
    //         ) {
    //           return o;
    //         }
    //         return null;
    //       });
    //     }

    //     this.instrumentsParams = _.sortBy(updateInstList, 'formal_name');
    //   }
    // },
    async confirmRemoval() {
      return this.$bvModal.msgBoxConfirm('Are you sure you want to remove the last instrument from the dataset? This will clear out the form and plot.', {
        title: 'Confirm removal of last instrument',
        size: 'lg',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES, REMOVE IT.',
        cancelVariant: 'primary',
        cancelTitle: 'NO, DO NOT REMOVE IT.',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      });
    },
    /**
     * Adds instrument to trace
     * @param {Object} - DOM Event object
     */
    addToTrace(e) {
      const ref = e.target.id.split('--')[0];
      const foundInst = _.find(this.selectedInstruments, {
        id: this.currentTrace.id,
        ref,
      });
      if (!foundInst) {
        this.addInstrument({
          id: this.currentTrace.id,
          ref,
        });
      }
    },
    /**
     * Removes instrument from trace
     * @param {Object} - DOM Event object
     */
    async removeFromTrace(e) {
      const ref = e.target.id.split('--')[0];
      const traceInstruments = this.getInstruments(this.currentTrace.id);
      if (traceInstruments.length === 1) {
        // When only one left confirm to remove first
        const isRemove = await this.confirmRemoval();
        if (isRemove) {
          this.handleOk(this.currentTrace.id, ref);
        }
      } else {
        this.handleOk(this.currentTrace.id, ref);
      }
    },
    /**
     * Handles remove confirmation
     * If user selects that they want to remove the last instrument, the program will:
     * Reset and clear out the form and chart
     * Remove the last selected instrument so the length is 0
     * @param {string} id - The trace id
     * @param {string} ref - The instrument reference designator to be removed
     */
    handleOk(id, ref) {
      this.resetTrace({ id });
      this.resetPlotLayout({
        uuid: this.uuid,
      });
      this.setHasDataFlag({ uuid: this.uuid, hasData: false });

      /** Clear out url queries that has to do with params */
      const query = {
        ...this.$route.query,
      };
      const finalQuery = _.omitBy(query, (value, key) => {
        if (['x', 'y'].includes(key) || key.includes('color') || key.includes('dt')) {
          return value;
        }
        return null;
      });

      this.removeInstrument({ id, ref });

      /** Reset url path */
      this.$router.push({ query: finalQuery });
    },
    clearTimeout() {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
    },
    setTimeout(callback) {
      this.clearTimeout();
      this.timeout = setTimeout(() => {
        this.clearTimeout();
        callback();
      }, 2000);
    },
    loadingPage() {
      this.loadPage = true;
      this.setTimeout(() => {
        this.loadPage = false;
      });
    },
    openInstCard(refDes, wpMap) {
      axios
        .get(`${APIBASEURL}/${APIMETADATA}/${APIINSTRUMENTS}`, {
          params: {
            refdes: refDes,
          },
        })
        .then((resp) => {
          // Data for instrument card details
          this.instrumentCard = resp.data;
        });
      this.fetchInstWPMapping(wpMap);
      this.fetchDeployments(refDes);
      this.fetchAnnotations(refDes);
      this.loadingPage();
      this.isHidden = true;
    },
    /** To get the image for each instrument page, there are two calls to make
     * 1. fetchInstWPMapping(wpMap) - Call the instrument page via the wpMap
     * information from the instrument endpoint
     * 2. fetchInstImage(featuredMeda) - Use the media tag to grab the image
     * source url from the static media page.
     */
    fetchInstWPMapping(wpMap) {
      if (!_.isEmpty(wpMap)) {
        // https://interactiveoceans.washington.edu/wp-json/wp/v2/pages/${wpMap}
        axios
          .get(`${process.env.OOIURL}/wp-json/wp/v2/pages/${wpMap}`)
          .then((resp) => {
            this.fetchInstImage(resp.data.featured_media);
          });
      }
    },
    fetchInstImage(featuredMedia) {
      // https://interactiveoceans.washington.edu/wp-json/wp/v2/media/${featuredMedia}
      axios
        .get(`${process.env.OOIURL}/wp-json/wp/v2/media/${featuredMedia}`)
        .then((resp) => {
          this.instImg = resp.data.source_url;
          this.loadingImg = false;
        });
    },
    /** This method calls the deployment endpoint with params: reference
     * designator.
     */
    fetchDeployments(refdes) {
      this.loadingDeployments = true;
      const params = {
        refdes,
      };
      axios
        .get(`${APIBASEURL}/${APIMETADATA}/get_deployments`, { params })
        .then((resp) => {
          this.processDeployments(resp.data);
          this.loadingDeployments = false;
        });
    },
    /** This method processes the date from the deployment end point for a
     * specific reference designator. The table will display the deployments
     * in order of most recent to oldest. The table headings will be (from
     * left to right): deployment number, UID, start date, end date, location
     * (latitude and longitude).
     */
    processDeployments(deployments) {
      const sortedDeployments = _.reverse(
        _.sortBy(deployments, ['deployment_number']),
      );
      this.deployments = sortedDeployments.map((dep) => {
        const newDep = {
          number: dep.deployment_number,
          UID: dep.uid,
          start_date: DateTime.fromMillis(dep.deployment_start)
            .toUTC()
            .toLocaleString(DateTime.DATETIME_FULL),
          end_date:
            dep.deployment_end !== null
              ? DateTime.fromMillis(dep.deployment_end)
                .toUTC()
                .toLocaleString(DateTime.DATETIME_FULL)
              : 'Ongoing',
          location: `(${dep.lat}, ${dep.lon})`,
        };
        return newDep;
      });
    },
    /** This method calls the annotation end point with params: reference
     * designator, start date and end date.
     */
    fetchAnnotations(refdes) {
      this.loadingAnnotations = true;
      const params = {
        ref: refdes,
        start_dt: DateTime.utc(2014, 1, 1, 0, 0, 0).toFormat('yyyyLLdd'),
        end_dt: DateTime.utc().toFormat('yyyyLLdd'),
      };
      axios
        .get(`${APIBASEURL}/${APIMETADATA}/get_annotations`, { params })
        .then((resp) => {
          this.processAnnotations(resp.data.annotations[refdes]);
          this.loadingAnnotations = false;
        });
    },
    /** This method processes the date from the annotation end point for a
     * specific reference designator. The table will display the annotations
     * in order of most recent to oldest. The annotation table will display the
     * annotation ID, reference designator, start date, end date, comment,
     * author, parameters, QC (quality control) code, and exclusion flag.
     */
    processAnnotations(annotationData) {
      this.annotations = annotationData.map((anno) => {
        const newRefList = [
          anno.site_rd,
          anno.infrastructure_rd,
          anno.instrument_rd,
          anno.stream_method,
          anno.stream_rd,
        ];
        const newAnno = {
          annotation_ID: anno.id,
          reference: _.join(
            _.remove(newRefList, (r) => r !== null),
            '-',
          ),
          start:
            anno.begin_date !== null
              ? DateTime.fromMillis(anno.begin_date)
                .toUTC()
                .toLocaleString(DateTime.DATE_FULL)
              : 'N/A',
          end:
            anno.end_date !== null
              ? DateTime.fromMillis(anno.end_date)
                .toUTC()
                .toLocaleString(DateTime.DATE_FULL)
              : 'N/A',
          comment: anno.annotation,
          author: anno.source,
          parameters:
            anno.parameters.length > 0 ? _.join(anno.parameters, ', ') : 'N/A',
          QC_flag: anno.qc_flag !== null ? _.startCase(anno.qc_flag) : 'N/A',
          exclude: anno.exclusion_flag ? 'Yes' : 'No',
        };
        return newAnno;
      });
    },
  },
};
</script>
<style scoped>
.card-header {
  color: #f2f2f2;
  font-weight: bold;
}
/* .card-deck {
  margin-top: 2.5rem;
} */
/* .card-body {
  background-color: #f2f2f2;
} */
.list-btn {
  /* background-color: #878787; */
  background-color: #383838;
  color: #f2f2f2;
  border-color: #878787;
}
.list-btn:hover {
  background-color: #3995a3;
  color: #f2f2f2;
}

/* Styling for the btn that will display features in progress */
.availability-btn {
  text-decoration: none;
  position: relative;
  display: inline-block;
}
/* Styling for the badge on features in progress */
.availability-btn .availability-badge {
  position: absolute;
  top: -15px;
  right: -30px;
  padding: 5px 10px;
  border-radius: 50%;
  background-color: #a33960;
  color: white;
  font-size: 0.5rem;
}
</style>
